import React from 'react';
import Head from 'next/head';
import includes from 'lodash/includes';
import get from 'lodash/get';

import MetaTags from '~/components/MetaTags/MetaTags';
import { initStore } from '~/state/store';
import { operations } from '~/state/team';
import { getTeamSchedule, CYCLE_TYPES } from '~/util/getTeamData';
import getLeagueInformation from '~/util/getLeagueInformation';
import withState from '~/hocs/withState';
import logEvent from '~/util/analytics';

import Layout from '~/components/Layout/Layout';
import TeamBanner from '~/components/TeamBanner/TeamBanner';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';
import TeamScheduleComponent from '~/components/TeamSchedule/TeamSchedule';

import css from '~/pages/team_schedule.scss';

interface Season {
  short_name: string;
}

interface League {
  current_season: Season;
}

interface Team {
  api_uri: string;
  id: string | number;
}

interface Props {
  league: League;
  team: Team;
  previousEvents: Object[];
  currentEvent: Object[];
  schedule: Object[];
  leagueSlug: string;
}

const getTeamIdFromState = state => get(state, 'pageData.team.id', '');

class TeamSchedule extends React.Component<Props> {
  static displayName: string;

  scrollableDiv: HTMLDivElement; // eslint-disable-line
  recentEvent: HTMLDivElement;

  static async getInitialProps(props) {
    if (props.leagueSlug) {
      const teamId = parseInt(props.query.teamId, 10);
      const team = { json: null };

      if (teamId !== getTeamIdFromState(props.store.getState())) {
        team.json = await props.store.dispatch(
          operations.setTeam(props.leagueSlug, props.query.teamId)
        );
      } else {
        team.json = props.store.getState().teamData.data;
      }

      if (!team.json || includes(team.json.api_uri, '/error/events')) {
        // eslint-disable-next-line
        props.res.statusCode = 404;
        return {};
      }

      const [previousEvents, currentEvent, schedule, league] = await Promise.all([
        getTeamSchedule(props.leagueSlug, teamId, CYCLE_TYPES.previous),
        getTeamSchedule(props.leagueSlug, teamId, CYCLE_TYPES.current),
        getTeamSchedule(props.leagueSlug, teamId),
        getLeagueInformation(props.leagueSlug),
      ]);

      return {
        league,
        previousEvents: previousEvents.json || [],
        currentEvent: currentEvent.json || [],
        schedule: schedule.json || [],
        leagueSlug: props.leagueSlug,
      };
    }
    return {};
  }

  constructor(props) {
    super(props);
    this.scrollableDiv = null;
    this.recentEvent = null;
  }

  componentDidMount() {
    logEvent('page_view', {
      league: this.props.leagueSlug,
      page_type: 'team_schedule',
      team_id: this.props.team.id,
    });

    if (window && this.recentEvent && this.scrollableDiv) {
      window.requestAnimationFrame(() => {
        this.scrollableDiv.scrollTop = this.recentEvent.offsetTop;
      });
    }
  }

  render() {
    const { leagueSlug, team, currentEvent, previousEvents, schedule, league } = this.props;

    return (
      <Layout>
        <Head>
          <style dangerouslySetInnerHTML={{ __html: css.stylesheet }} />
        </Head>
        <MetaTags content={team} type="team" />
        <div className={css.desktopView}>
          <Ad leagueSlug={leagueSlug} adType={AdTypes.LEADERBOARD} />
        </div>
        <TeamBanner team={team} currentSection="schedule" />
        <div
          className={css.teamScheduleComponent}
          ref={e => (this.scrollableDiv = e)}
          id="teamScheduleComponent"
        >
          <div className={css.league}>{get(league, 'current_season.short_name', '')}</div>
          <TeamScheduleComponent
            team={team}
            events={schedule}
            mostRecentEvent={currentEvent[0] || previousEvents[0]}
            recentRef={e => (this.recentEvent = e)}
          />
        </div>
      </Layout>
    );
  }
}

TeamSchedule.displayName = 'TeamSchedulePage';

const mapStateToProps = state => ({
  team: state.teamData.data,
});

export default withState({ mapStateToProps, createStore: initStore })(TeamSchedule);
